import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";

import { onboardingSiteLocationPrefix } from "./urls";

export const signInUrl = `${onboardingSiteLocationPrefix}/auth/login`;
export const signOutUrl = `${onboardingSiteLocationPrefix}/auth/logout`;
export const switchAccountUrl = `${onboardingSiteLocationPrefix}?prompt=login`;

/**
 * Redirect to sign-in, preserving tenantId and all other query params (accessid, ticketnumber, etc.)
 * in the callback URL. Use this whenever a page detects the user is not signed in.
 */
export const redirectToSignIn = () => {
  const params = new URLSearchParams(window.location.search);
  const tenantId = params.get("tenantId") || params.get("tenantid");
  const loginEmail = params.get("loginEmail") || params.get("loginemail");

  const callbackParams = new URLSearchParams(params);
  callbackParams.delete("tenantId");
  callbackParams.delete("tenantid");
  callbackParams.delete("loginEmail");
  callbackParams.delete("loginemail");
  const callbackQuery = callbackParams.toString();
  const callbackPath = `${window.location.origin}${window.location.pathname}${
    callbackQuery ? `?${callbackQuery}` : ""
  }`;

  const tenantParam = tenantId ? `&tenantId=${tenantId}` : "";
  const loginEmailParam = loginEmail ? `&loginEmail=${encodeURIComponent(loginEmail)}` : "";
  window.location.href = `${signInUrl}?callbackUrl=${encodeURIComponent(
    callbackPath
  )}${tenantParam}${loginEmailParam}`;
};

/**
 * Hook that redirects to sign-in if the user is not authenticated.
 * Returns `true` if the user is signed in, `false` if redirecting.
 * Use at the top of any page component that requires authentication.
 */
export const useRequireAuth = (): boolean => {
  const isSignedIn = useSelector(getIsUserSignedIn);

  useEffect(() => {
    if (typeof window !== "undefined" && !isSignedIn) {
      redirectToSignIn();
    }
  }, [isSignedIn]);

  return !!isSignedIn;
};

export const useAuthenticatedNavigation = (targetPath: string) => {
  const [signInUrlWithCallback, setSignInUrlWithCallback] = useState("");
  const isSignedIn = useSelector(getIsUserSignedIn);

  useEffect(() => {
    if (isSignedIn) {
      // If signed in, return the direct target path
      setSignInUrlWithCallback(`${window.location.origin}${targetPath}`);
    } else {
      // If not signed in, return sign-in URL with callback
      setSignInUrlWithCallback(`${signInUrl}?callbackUrl=${window.location.origin}${targetPath}`);
    }
  }, [targetPath, isSignedIn]);

  return signInUrlWithCallback;
};
